import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {reduxForm, formValueSelector} from 'redux-form';
import { createLabelHelper } from 'infrastructure/js/utils/labelHelper';
import Validation from 'infrastructure/js/components/controls/controlsValidations.js';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import TextField from 'infrastructure/js/components/controls/TextField/textField';
import Combobox from 'infrastructure/js/components/controls/Combobox/combobox.js';
import TimeField from 'infrastructure/js/components/controls/TimeField/timeField';
import List from 'infrastructure/js/components/List/list';
import DateTimeHelper from 'infrastructure/js/utils/dateTimeHelper';
import Normalize from 'infrastructure/js/components/controls/controlsNormalizations.js';
import UnitHelper, { unitTypes } from 'infrastructure/js/utils/uomHelper';
import PermissionManager from 'infrastructure/js/utils/permissionManager';
import Tooltip from 'infrastructure/js/components/tooltip/tooltip.js';
import { components } from 'react-select';
import {compose} from 'redux';
import Header, {headerItemsLabels} from '../../../Common/Header/header';
import Tabs from 'infrastructure/js/components/Tabs/tabs';
import {Prompt} from 'react-router-dom';
import Overlay from 'infrastructure/js/components/Overlay/overlay';
import RangeListItem, { operatorOptions } from '../../../Common/Controls/AddRemoveList/AddRemoveListItem/RangeListItem/rangeListItem';
import withPortal from 'infrastructure/js/components/HOCs/withPortal/withPortal';
import EntitiesMultiSelect from '../../../Common/Controls/EntitiesMultiSelect/entitiesMultiSelect';
import {EntityPropertyTypes} from '../../../../enums/entityPropertyTypes';
import {FetchEntitiesFilters} from '../../../../enums/fetchEntitiesFilters';

import './materialTypeForm.scss';

const EntitiesMultiSelectPortal = withPortal(EntitiesMultiSelect);

const TabKeys = {
  DETAILS: 'DETAILS',
  QPL: 'QPL',
};

class MaterialTypeForm extends React.PureComponent {
  constructor(props) {
    super(props);

    this.labels = createLabelHelper('mat.administration.matsettings.materialtypes.materialTypeForm.');
    this.defrostingRangesUOMOptions = [
      { label: UnitHelper.getLabelForMajorUnitType(unitTypes.LENGTH), value: unitTypes.LENGTH },
      { label: UnitHelper.getLabelForMajorUnitType(unitTypes.AREA), value: unitTypes.AREA },
    ];

    this.itemToEdit = props.initialData;
    this.isEditMode = ('edit' === props.match.params.mode) && !!this.itemToEdit;
    this.renderETLBond = PermissionManager.isEtlBondEnabled();
    this.majorUOMType = PermissionManager.isWeightSupported() ? unitTypes.WEIGHT : unitTypes.LENGTH;
    this.majorQplUOMType = PermissionManager.isWeightSupported() ? unitTypes.WEIGHT : unitTypes.LENGTH;

    if(!PermissionManager.isWeightSupported() && this.isEditMode && this.itemToEdit.defrostingRangeRules){
      this.majorUOMType = this.itemToEdit.defrostingRangeRules.areaUnits ? unitTypes.AREA : unitTypes.LENGTH;
    }

    this.state = {
      majorUOMType: this.majorUOMType,
      majorQplUOMType: this.majorQplUOMType,
      selectedTab: TabKeys.DETAILS,
      confirmedAbandon: false,
    }

    this.fetchConfig = {
      entityType: EntityPropertyTypes.QPL_NAME_VERSION,
      filter: [FetchEntitiesFilters.ALL],
      action: this.props.actions.fetchEntities,
    };
  }

  componentDidMount() {

    this.props.actions.fetchFormData();

    let initialValues = {};
    let defaultDefrostingTime = PermissionManager.getOrgPreferences().defaultDefrostingTime;
    if(defaultDefrostingTime){
      initialValues.defrostingTime_minutes = defaultDefrostingTime % 60 | 0;
      initialValues.defrostingTime_hours = defaultDefrostingTime / 60 | 0;
    }
    if (this.isEditMode) {
      initialValues = {
        materialCode: this.itemToEdit.businessId,
        materialName: {value: this.itemToEdit.id, label: this.itemToEdit.materialName, data: this.itemToEdit},
        expiresWithinDays: this.itemToEdit.expiresWithinDays,
        defrostingDecayRate: this.itemToEdit.defrostingDecayRate ?? null,
        defrostingRanges: this.getInitialDefrostingRanges(),
        defrostingRangesUOM: this.majorUOMType,
        qplRangesUOM: this.majorQplUOMType,
      };
      let _width = this.itemToEdit.width;
      if (null !== _width && undefined !== _width) {
        initialValues.width = UnitHelper.serverValueToUserValue(unitTypes.WIDTH, _width, 2);
      }
      let _maxStorageTemperature = this.itemToEdit.maxStorageTemp;
      if (null !== _maxStorageTemperature && undefined !== _maxStorageTemperature) {
        initialValues.maxStorageTemp = UnitHelper.serverValueToUserValue(unitTypes.TEMPERATURE, _maxStorageTemperature, 0);
      }
      if(this.itemToEdit.defrostingTime !== null){
        initialValues.defrostingTime_minutes = this.itemToEdit.defrostingTime % 60 | 0;
        initialValues.defrostingTime_hours = this.itemToEdit.defrostingTime / 60 | 0;
      }
      if(this.itemToEdit.maxExposureTimeBond !== null){
        initialValues.maxExposureTimeBond_minutes = this.itemToEdit.maxExposureTimeBond % 60 | 0;
        initialValues.maxExposureTimeBond_hours = this.itemToEdit.maxExposureTimeBond / 60 | 0;
      }
      if(this.itemToEdit.maxExposureTimeCure !== null){
        initialValues.maxExposureTimeCure_minutes = this.itemToEdit.maxExposureTimeCure % 60 | 0;
        initialValues.maxExposureTimeCure_hours = this.itemToEdit.maxExposureTimeCure / 60 | 0;
      }
      if (PermissionManager.isQplEnabled() || PermissionManager.isCoCEnabled()) {
        initialValues.qplRanges = this.getInitialQplRanges();
      }
    }

    this.props.initialize(initialValues);
  }

  componentWillUnmount() {
    this.props.actions.clearFormData();
  }

  getInitialQpl(qpl) {
    return {
        assetsType: {
          value: 'QPL',
          label: 'qpl',
        },
        assetsValue: {
          value: qpl.id,
          label: qpl.name + ' (' + qpl.version + ')',
          data: qpl
        }
    }
  }


  getInitialQplRanges() {
    if (this.isEditMode && this.itemToEdit?.qplRules) {
      return this.itemToEdit.qplRules.map((rule) => {
        const {comparisonOperator, arguments: _arguments, qpl} = rule;

        const minQuantity = _arguments?.length === 2 ? _arguments[0] : undefined;
        const quantity = _arguments?.length === 2 ? _arguments[1] : _arguments[0];
        return {
          operator: operatorOptions.find((op) => op.value === comparisonOperator),
          quantity,
          minQuantity,
          qpl: this.getInitialQpl(qpl)
        }
      });
    }
  }

  getInitialDefrostingRanges() {
    if(this.isEditMode && this.itemToEdit?.defrostingRangeRules?.rules){
      return this.itemToEdit.defrostingRangeRules.rules.map((rule) => {
        // aliasing arguments to _arguments to avoid conflict with arguments keyword
        const { comparisonOperator, arguments: _arguments, defrostingTimeInMinutes } = rule;

        let minQuantity, quantity;

        if(_arguments.length === 2){
          minQuantity = _arguments[0];
          quantity = _arguments[1];
        } else {
          minQuantity = undefined;
          quantity = _arguments[0];
        }

        return {
          operator: operatorOptions.find((op) => op.value === comparisonOperator),
          quantity,
          minQuantity,
          defrostingTime: {
            _hours: defrostingTimeInMinutes / 60 | 0,
            _minutes: defrostingTimeInMinutes % 60 | 0,
          }
        };
      });
    }

    return [];
  }

  getMaterials() {
    let formData = this.props.sData?.get('formData');
    return formData?.materials ?? [];
  }

  getDefrostingRanges(defrostingRanges) {
    if(!defrostingRanges?.length){
      return null;
    }

    return defrostingRanges?.map((range) => {
      const { operator, minQuantity, quantity, defrostingTime } = range;
      return {
        comparisonOperator: operator.value,
        arguments: minQuantity ? [minQuantity, quantity] : [quantity],
        defrostingTimeInMinutes: DateTimeHelper.ConvertHoursMinutesToMinutes(defrostingTime._hours, defrostingTime._minutes),
      };
    });
  }

  onSubmit = (data) => {

    let materialNameLabel = data.materialName ? data.materialName.label : null;
    let defrostingRangeRules = null;
    if(data.defrostingRanges?.length){
      defrostingRangeRules = {
        areaUnits: this.state.majorUOMType === unitTypes.AREA,
        rules: this.getDefrostingRanges(data.defrostingRanges)
      };
    }

    let qplRules = null;
    if (data.qplRanges?.length) {
      qplRules = data.qplRanges.map(item => {
        const { operator, minQuantity, quantity, qpl } = item;

        return {
          comparisonOperator: operator?.value,
          quantityArguments: (minQuantity || minQuantity === 0) ? [minQuantity, quantity] : [quantity],
          qplId: qpl.assetsValue?.data?.id,
      }})
    }

    let newData = {
      businessId: data.materialCode,
      materialName: materialNameLabel,
      defrostingTime: DateTimeHelper.ConvertHoursMinutesToMinutes(data.defrostingTime_hours, data.defrostingTime_minutes),
      defrostingDecayRate: (data.defrostingDecayRate || data.defrostingDecayRate === 0) ? data.defrostingDecayRate : null,
      width: (data.width || data.width === 0) ? UnitHelper.userValueToServerValue(unitTypes.WIDTH, data.width, 2) : null,
      maxExposureTimeBond: DateTimeHelper.ConvertHoursMinutesToMinutes(data.maxExposureTimeBond_hours, data.maxExposureTimeBond_minutes),
      maxExposureTimeCure: DateTimeHelper.ConvertHoursMinutesToMinutes(data.maxExposureTimeCure_hours, data.maxExposureTimeCure_minutes),
      expiresWithinDays: data.expiresWithinDays,
      maxStorageTemp: UnitHelper.userValueToServerValue(unitTypes.TEMPERATURE, data.maxStorageTemp, 0),
      defrostingRangeRules,
      qplRules
    };

    if (this.isEditMode) {
      newData.id = this.itemToEdit.id;
      newData.orgId = this.itemToEdit.orgId;
    }

    this.props.actions.submit(newData, this.isEditMode, this.onSubmitSucceeded);
  };

  onSubmitSucceeded = () => {
    this.setState({ confirmedAbandon: true }, () => {
      this.props.history.replace('/administration/matsettings/materialTypes')
    });
  }

  onHide =() => {
    this.props.actions.hide();
  };

  onMajorUOMChange = ({value}) => {
    this.setState({ majorUOMType: value });
  }

  getLabelExtraComponent = (value) => {
    return (
      <span className="label-extra-component">{value}</span>
    )
  }

  maxLength50 = Validation.maxLength(50);
  maxLengthDropDown = Validation.dropdown.maxLength(50);

  renderMaxExposureTimeBond = () => {
    return (
      <InputSection label={this.labels.get('maxExposureTimeBond')} htmlFor="maxExposureTimeBond" className="inline left-side">
        <TimeField id="maxExposureTimeBond" name="maxExposureTimeBond" hoursCount={true} maxHoursLength={3} />
      </InputSection>);
  }

  renderMaxExposureTimeCure = () => {
    return (
      <InputSection label={this.labels.get('maxExposureTimeCure')} htmlFor="maxExposureTimeCure" className="inline left-side">
        <TimeField id="maxExposureTimeCure" name="maxExposureTimeCure" hoursCount={true} maxHoursLength={3} />
      </InputSection>
    );
  }

  renderMaxTemperature = () => {
    let side = this.renderETLBond ? 'right-side' : 'left-side';
    return (
      <InputSection label={this.labels.get('maxStorageTemperature', undefined, { units: UnitHelper.getLabelForUnitType(unitTypes.TEMPERATURE) })}  htmlFor="maxStorageTemp" className={"inline " + side}>
        <TextField id="maxStorageTemp" name="maxStorageTemp" className="short-textfield" normalize={Normalize.number(true,  UnitHelper.getMinValueForUnitType(unitTypes.TEMPERATURE, 0),  UnitHelper.getMaxValueForUnitType(unitTypes.TEMPERATURE, 0))}/>
      </InputSection>);
  }

  listItemRenderer = (data) => {
    return (
      <RangeListItem
        {...data}
        unitType={this.state.majorUOMType}
        {...this.props}
      >
          {({ name }) => (
            <>
              <TimeField
                id={name + '.[defrostingTime]'}
                name={name + '.[defrostingTime]'}
                hoursCount={true}
                maxHoursLength={3}
                validate={Validation.timeField.required}
              />
            </>
          )}
      </RangeListItem>
    )
  };

  qplListItemRenderer = (data) => {
    return (
      <RangeListItem
        {...data}
        unitType={this.state.majorQplUOMType}
        {...this.props}
        isViewOnly={PermissionManager.isCoCEnabled()}
      >
        {({ name }) => (
            <>
              <EntitiesMultiSelectPortal
                  id={name + '.[qpl]'}
                  name={name + '.[qpl]'}
                  entitiesTypes={['QPL']}
                  fetchConfig={this.fetchConfig}
                  change={this.props.change}
                  showEntitiesTypes={false}
                  isMulti={false}
                  validate={Validation.dropdown.required}
                  isDisabled={PermissionManager.isCoCEnabled()}
                  classNames={{
                    menuPortal: () => 'qpl-select-menu-portal',
                  }}
              />
            </>
        )}
      </RangeListItem>
    )
  };

  handleAbandon = (nextLocation) => {
    if (!this.state.confirmedAbandon) {
      this.props.actions.abandonDirtyForm(() => {
        this.setState({ confirmedAbandon: true }, () => {
          this.props.history.replace(nextLocation.pathname);
        });
      });
      return false;
    }
    return true;
  };


  getHeaderItems = () => {
    return {
      buttons: [
        {
          id: 'cancel',
          label: headerItemsLabels.CANCEL,
          className: 'no-icon',
          action: () => this.props.history.replace(`/Administration/matSettings/materialTypes`),
        },
        {
          id: 'save',
          label: headerItemsLabels.SAVE,
          className: 'no-icon',
          action: this.props.handleSubmit(this.onSubmit),
          disabled: (this.isEditMode && this.props.pristine || this.props.sData.get('loading'))
        },
      ],
    };
  };

  onTabClick = (selectedTab) => {
    const submitFunc = this.props.handleSubmit(() => this.setState({ selectedTab }));

    // If there are any validation errors the handleSubmit callback is NOT invoked.
    const invalidFields = submitFunc();

    // If any of the current tab's fields are invalid prevent moving on to other tabs.
    if (invalidFields && this.isCurrentTabValid(invalidFields)) {
      this.setState({ selectedTab });
    }
  };

  isCurrentTabValid = (invalidFields = {}) => {
    const currentTab = this.state.selectedTab;
    const tabsValidationFields = {
      DETAILS: ['materialCode', 'defrostingRanges',],
      QPL: ['qplRanges'],
    };

    const validationFields = tabsValidationFields[currentTab];
    const isCurrentTabInvalid = Object.keys(invalidFields).some((field) => validationFields.includes(field));

    return !isCurrentTabInvalid;
  };

  getTitle = () => {
    if (this.isEditMode) {
      return  `${this.labels.get('title.edit')} - ${this.itemToEdit.businessId}`;
    }


    return this.labels.get('title.create');
  }

  renderDetailsTab = () => {
    return (
      <Tabs.Tab className="details-tab" eventKey={TabKeys.DETAILS} title={this.labels.get('tabs.details')}
                animation={false}>
        <div className="modal-body">

          <InputSection label={this.labels.get('code') + '*'} htmlFor="materialCode"
                        className="inline left-side no-margin">
            <TextField id="materialCode" name="materialCode" className="short-textfield"
                       validate={[Validation.required, this.maxLength50]}
            />
          </InputSection>

          <InputSection label={this.labels.get('name')} htmlFor="materialName" className="inline right-side no-margin">
            <Combobox id="materialName" name="materialName" options={this.getMaterials()}
                      allowNewOption={true}
                      validate={this.maxLengthDropDown}
            />
          </InputSection>
          <div className='input-section inline left-side'>
            <div className='defrosting-section'>
              <InputSection label={this.labels.get('defrostingTime')} htmlFor="defrostingTime" className="no-margin">
                <TimeField id="defrostingTime" name="defrostingTime" hoursCount={true} maxHoursLength={3}/>
              </InputSection>

              <InputSection label={this.labels.get('defrostingDecayRate')} htmlFor="defrostingDecayRate"
                            className="no-margin">
                <TextField id="defrostingDecayRate" name="defrostingDecayRate"
                           normalize={Normalize.number(true, 0, 100)}/>
              </InputSection>
            </div>
          </div>

          <InputSection label={this.labels.get('width')} htmlFor="width" className="inline right-side"
                        labelExtraComponent={this.getLabelExtraComponent(`(${UnitHelper.getLabelForUnitType(unitTypes.WIDTH)})`)}>
            <TextField id="width" name="width" className="short-textfield"
                       normalize={Normalize.number(false, 0, UnitHelper.getMaxValueForUnitType(unitTypes.WIDTH))}/>
          </InputSection>

          {(this.renderETLBond ? this.renderMaxExposureTimeBond() : this.renderMaxExposureTimeCure())}

          <InputSection label={this.labels.get('expiresWithinDays')} htmlFor="expiresWithinDays"
                        className="inline right-side">
            <TextField id="expiresWithinDays" name="expiresWithinDays" normalize={Normalize.number(true, 0, 99999)}/>
          </InputSection>

          {this.renderETLBond ? this.renderMaxExposureTimeCure() : null}
          {this.renderMaxTemperature()}

          <div className="defrosting-ranges">
            <div className="defrosting-ranges__header">
              <div className="defrosting-ranges__column-header">
                <label>
                  {this.labels.get('defrostingRangeList.header.range') + '*'} ({UnitHelper.getLabelForUnitType(this.state.majorUOMType)})
                  {this.state.majorUOMType !== unitTypes.WEIGHT && (
                    <Combobox
                      id="defrostingRangesUOM"
                      name="defrostingRangesUOM"
                      options={this.defrostingRangesUOMOptions}
                      onChangeCallback={this.onMajorUOMChange}
                      dropdownIndicatorRenderer={DropdownIndicator}
                      isSearchable={false}
                      placeholder={null}
                    />
                  )}
                </label>
              </div>
              <label>{this.labels.get('defrostingRangeList.header.time') + '*'}</label>
            </div>

            <List
              name="defrostingRanges"
              itemRenderer={this.listItemRenderer}
              addButtonLabel={this.labels.get('addRange.button')}
              maxItemsToRender={10}
            />
          </div>
        </div>
      </Tabs.Tab>
    )
  }

  renderQPLs = () => {
    return (
      <div className="qpls-list">
        <div className="qpls-list__header">
          <label>{this.labels.get('qplRangeList.header.name') + '*'}</label>
          <label>{this.labels.get('qplRangeList.header.quantityType') + '* ('+ UnitHelper.getLabelForUnitType(this.state.majorQplUOMType) + ')'}</label>
        </div>

        <List
          name="qplRanges"
          itemRenderer={this.qplListItemRenderer}
          addButtonLabel={this.labels.get('addQpl.button')}
          isViewOnly={PermissionManager.isCoCEnabled()}
        />
      </div>
    )
  }

  renderQplTab = () => {
    if (PermissionManager.isQplEnabled() || PermissionManager.isCoCEnabled()) {
      return (
        <Tabs.Tab className="qpl-tab" eventKey={TabKeys.QPL} title={this.labels.get('tabs.qpl')}
                  animation={false}>
          <div className="modal-body">
            {this.renderQPLs()}
          </div>
        </Tabs.Tab>
      )
    }
    return null;
  }

  renderLoadingOverlay = () => {
    if (!this.props.sData.get('loading')) {
      return null;
    }
    return <Overlay.Loading/>;
  };

  render() {
    let selectedTab = this.state.selectedTab;
    const headerItems = this.getHeaderItems();

    return (
      <div className="pl-form material-type-form dialog">
        <div className="title">{this.getTitle()}</div>

        <div className='header-wrapper'>
          <Header className='form-header' {...headerItems} />
        </div>

        <div className='tabs-content'>
          <Tabs activeKey={selectedTab} tabType="form" className='material-type-form-tabs' onSelect={this.onTabClick}
                id="content-tabs" mountOnEnter={false} unmountOnExit={false}>
            {this.renderDetailsTab()}
            {this.renderQplTab()}
          </Tabs>
        </div>
        <Prompt when={!this.props.pristine} message={this.handleAbandon}/>
        {this.renderLoadingOverlay()}
      </div>
    );
  }
}

MaterialTypeForm.propTypes = {
  actions: PropTypes.object.isRequired,
  sData: PropTypes.object.isRequired,
};

const selector = formValueSelector('materialTypeForm');
const mapStateToProps = (state) => {
  return {
    defrostingRanges: selector(state, 'defrostingRanges'),
    qplRanges: selector(state, 'qplRanges'),
  };
};


const reduxFormConfig = {
  form: 'materialTypeForm'
};

export default compose(connect(mapStateToProps), reduxForm(reduxFormConfig), withRouter)(MaterialTypeForm);


const DropdownIndicator = forwardRef((params, ref) => {
  return (
    <Tooltip placement="top" value={'Select Unit Type'} delayShow={500}>
      <div>
        <components.DropdownIndicator {...params} ref={ref}/>
      </div>
    </Tooltip>
  );
})

DropdownIndicator.displayName = 'DropdownIndicator';
